import { generateCacheKey, invalidateCache } from "@punchup/utils/caches";
import {
  getComedianByIDOrThrow,
  getComedianBySlugOr404,
} from "../../legacy/comedian";
import { getSpecialByID, getSpecialLite } from "@punchup/data/legacy/specials";

import { CacheEntities } from "@punchup/utils/caches/cache-types";
import { getCollectionIDsForSpecial } from "../public/collectionSpecials";
import { logException } from "@punchup/sentry";
import { revalidatePath } from "next/cache";

/**
 * Server Only: clears caches for a comedian given their id
 */
export async function SERVER_clearComedianCachesUsingId(comedianId: string) {
  const comedian = await getComedianByIDOrThrow(comedianId);
  const cacheKeyId = generateCacheKey(CacheEntities.COMEDIAN, comedianId);
  const cacheKeySlug = generateCacheKey(CacheEntities.COMEDIAN, comedian.slug);
  await Promise.all([
    await invalidateCache(cacheKeyId),
    await invalidateCache(cacheKeySlug),
  ]);
  revalidatePath(`/${comedian.slug}`);
  revalidatePath(`/api/published-comedians`);
}

/**
 * Server Only: clears caches for a comedian given their slug
 */
export async function SERVER_clearComedianCachesUsingSlug(slug: string) {
  const comedian = await getComedianBySlugOr404(slug);
  const cacheKeySlug = generateCacheKey(CacheEntities.COMEDIAN, slug);
  const cacheKeyId = generateCacheKey(CacheEntities.COMEDIAN, comedian.id);
  await Promise.all([
    await invalidateCache(cacheKeySlug),
    await invalidateCache(cacheKeyId),
  ]);
  revalidatePath(`/${slug}`);
  revalidatePath(`/api/published-comedians`);
}

/**
 * Server Only: revalidate a comedian's public profile after a representation-contact
 * write (INB-51). Uses the "layout" variant so the whole `(landing)` subtree refreshes —
 * the `/[slug]/contacts` tab where the contacts render, the root `/[slug]` page, AND the
 * shared landing layout that conditionally shows/hides the "Contacts" nav tab (a first add
 * or last delete flips that visibility, so a page-only revalidate would leave the nav
 * stale). No custom entity cache is involved: the public contacts fetcher is an uncached
 * anon query, so only the Next.js ISR page cache needs busting.
 */
export async function SERVER_clearComedianContactsCache(comedianId: string) {
  const comedian = await getComedianByIDOrThrow(comedianId);
  revalidatePath(`/${comedian.slug}`, "layout");
}

/**
 * Server Only: clears caches for a special given the id
 */
export async function SERVER_clearSpecialCacheUsingId(specialId: string) {
  const special = await getSpecialByID(specialId);
  const comedian = await getComedianByIDOrThrow(special.comedian_id);
  const cacheKeyId = generateCacheKey(CacheEntities.SPECIAL, specialId);
  const cacheKeySlug = generateCacheKey(CacheEntities.SPECIAL, special.slug);
  await Promise.all([
    await invalidateCache(cacheKeyId),
    await invalidateCache(cacheKeySlug),
  ]);
  revalidatePath(`/${comedian.slug}/${special.slug}`);
  revalidatePath(`/api/published-specials`);
}

/**
 * Server Only: clears caches for a special given the slug
 */
export async function SERVER_clearSpecialCacheUsingSlug(specialSlug: string) {
  const special = await getSpecialLite(specialSlug);
  const comedian = await getComedianByIDOrThrow(special.comedian_id);
  const cacheKeyId = generateCacheKey(CacheEntities.SPECIAL, special.id);
  const cacheKeySlug = generateCacheKey(CacheEntities.SPECIAL, specialSlug);
  await Promise.all([
    await invalidateCache(cacheKeySlug),
    await invalidateCache(cacheKeyId),
  ]);
  revalidatePath(`/${comedian.slug}/${specialSlug}`);
  revalidatePath(`/api/published-specials`);
}

export type ComedianSpecialsTagsMode = "none" | "include" | "only";

export function collectionSpecialsCacheKey(
  collectionId: string,
  cursor: number,
  limit: number
): string {
  return generateCacheKey(CacheEntities.COLLECTION_SPECIALS, collectionId, {
    cursor,
    limit,
  });
}

export function collectionSpecialsNewestCacheKey(
  collectionId: string,
  limit: number
): string {
  return generateCacheKey(CacheEntities.COLLECTION_SPECIALS, collectionId, {
    sort: "newest",
    limit,
  });
}

export function collectionLatestEpisodeCacheKey(collectionId: string): string {
  return generateCacheKey(CacheEntities.COLLECTION_SPECIALS, collectionId, {
    latest: true,
  });
}

export function comedianSpecialsCacheKey(
  comedianId: string,
  cursor: string,
  limit: number,
  tagsMode: ComedianSpecialsTagsMode
): string {
  return generateCacheKey(CacheEntities.COMEDIAN, comedianId, {
    cursor,
    limit,
    tagsMode,
  });
}

/**
 * The listing params the public pages actually request — one entry per caller.
 * A caller with other params is only refreshed by the 5-minute TTL, so a new
 * caller of /api/v2/public/specials adds its params here.
 */
const PUBLIC_COLLECTION_PAGES = [
  { cursor: 0, limit: 100 }, // bundle + special pages
  { cursor: 0, limit: 20 }, // checkout list (API default)
];
const PUBLIC_COLLECTION_NEWEST_LIMITS = [12]; // library podcast groups
const PUBLIC_COMEDIAN_PAGES: {
  cursor: string;
  limit: number;
  tagsMode: ComedianSpecialsTagsMode;
}[] = [
  { cursor: "", limit: 500, tagsMode: "include" }, // public Videos tab
  { cursor: "", limit: 20, tagsMode: "none" }, // messaging picker (API default)
];

function collectionListingCacheKeys(collectionId: string): string[] {
  return [
    ...PUBLIC_COLLECTION_PAGES.map(({ cursor, limit }) =>
      collectionSpecialsCacheKey(collectionId, cursor, limit)
    ),
    ...PUBLIC_COLLECTION_NEWEST_LIMITS.map((limit) =>
      collectionSpecialsNewestCacheKey(collectionId, limit)
    ),
    collectionLatestEpisodeCacheKey(collectionId),
  ];
}

function comedianListingCacheKeys(comedianId: string): string[] {
  return PUBLIC_COMEDIAN_PAGES.map(({ cursor, limit, tagsMode }) =>
    comedianSpecialsCacheKey(comedianId, cursor, limit, tagsMode)
  );
}

async function invalidateCacheKeys(keys: string[]): Promise<void> {
  await Promise.all(keys.map((key) => invalidateCache(key)));
}

/**
 * Server Only: refreshes a collection's public video list — and the comedian's
 * Videos tab, which carries each video's collection ids — after Manage Content
 * changes its videos or publish state. Best-effort: a failed clear only delays
 * the change until the TTL.
 */
export async function SERVER_clearCollectionListingCaches(
  collectionId: string,
  comedianId: string
): Promise<void> {
  await invalidateCacheKeys([
    ...collectionListingCacheKeys(collectionId),
    ...comedianListingCacheKeys(comedianId),
  ]).catch(logException);
}

/**
 * Server Only: refreshes every public listing a special appears in — the
 * comedian's Videos tab and each collection holding it — after Manage Content
 * publishes, unpublishes, or edits it. Best-effort, like the collection clear.
 */
export async function SERVER_clearSpecialListingCaches(
  specialId: string,
  comedianId: string
): Promise<void> {
  try {
    const collectionIds = await getCollectionIDsForSpecial(specialId);
    await invalidateCacheKeys([
      ...collectionIds.flatMap(collectionListingCacheKeys),
      ...comedianListingCacheKeys(comedianId),
    ]);
  } catch (error) {
    logException(error);
  }
}
